import { useState, useEffect, useRef } from 'react';
import { NavBar } from '@/components/NavBar';
import { Hero } from '@/components/Hero';
import { BuyerPaths } from '@/components/BuyerPaths';
import { Residences } from '@/components/Residences';
import { LagoonAmenities } from '@/components/LagoonAmenities';
import { LocationMap } from '@/components/LocationMap';
import { CTASection } from '@/components/CTASection';
import { Footer } from '@/components/Footer';
import { EnquiryRail } from '@/components/EnquiryRail';
import { LeadForm } from '@/components/LeadForm';
import { MeetingModal } from '@/components/MeetingModal';
import { WhatsAppButton } from '@/components/WhatsAppButton';
import { StickyMobileCTA } from '@/components/StickyMobileCTA';
import { PasswordGate } from '@/components/PasswordGate';
import { SplashScreen } from '@/components/SplashScreen';
import { VimeoEmbedSection } from '@/components/VimeoEmbedSection';
import { initAnalytics, trackPageView, trackEvent } from '@/lib/analytics';
import { getPasswordProtectionEnabled } from '@/lib/site-config';
import {
  getOrganizationSchema,
  getWebSiteSchema,
  getPlaceSchema,
  getFAQSchema,
  getBreadcrumbSchema,
} from '@/lib/schema';

const Index = () => {
  const [leadFormOpen, setLeadFormOpen] = useState(false);
  const [meetingOpen, setMeetingOpen] = useState(false);
  const [passwordGateOpen, setPasswordGateOpen] = useState(false);
  const [passwordProtectionEnabled, setPasswordProtectionEnabled] = useState(false);
  const [authenticated, setAuthenticated] = useState(() => {
    // Initialize from localStorage immediately to avoid flash
    if (typeof window !== 'undefined') {
      return localStorage.getItem('pearl_vip_authenticated') === 'true';
    }
    return false;
  });
  const [loading, setLoading] = useState(true);
  const [showSplash, setShowSplash] = useState(() => {
    // Check if user has seen splash before (optional - can be removed if splash should always show)
    if (typeof window !== 'undefined') {
      const hasSeenSplash = localStorage.getItem('pearl_splash_seen') === 'true';
      return !hasSeenSplash;
    }
    return true;
  });

  // Hero ref for IntersectionObserver
  const heroRef = useRef<HTMLElement>(null);

  // WhatsApp number - in production this would come from env variable
  const whatsappNumber = '13451234567'; // Replace with actual number
  const whatsappMessage = encodeURIComponent(
    "Hi! I'm interested in learning more about The Pearl at Grand Harbour."
  );

  useEffect(() => {
    // Check password protection status and authentication
    const checkAccess = async () => {
      let timeoutId: NodeJS.Timeout | null = null;
      
      // Set a timeout to prevent infinite loading if the request hangs
      const timeoutPromise = new Promise<boolean>((resolve) => {
        timeoutId = setTimeout(() => {
          console.warn('Password protection check timed out after 5s, using fallback');
          resolve(false);
        }, 5000);
      });

      try {
        // Race between the actual call and timeout
        const isEnabled = await Promise.race([
          getPasswordProtectionEnabled(),
          timeoutPromise
        ]);
        
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        
        const isAuth = typeof window !== 'undefined' && 
          localStorage.getItem('pearl_vip_authenticated') === 'true';
        
        if (import.meta.env.DEV) {
          console.log('Password protection check:', { 
            isEnabled, 
            isAuth, 
            localStorage: localStorage.getItem('pearl_vip_authenticated'),
            willShowGate: isEnabled && !isAuth
          });
        }
        
        setPasswordProtectionEnabled(isEnabled);
        setAuthenticated(isAuth);
        
        // If protection is enabled and user is not authenticated, prevent scrolling
        if (isEnabled && !isAuth) {
          document.body.style.overflow = 'hidden';
        } else {
          document.body.style.overflow = '';
        }
      } catch (error) {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        console.error('Error checking access:', error);
        // Fallback to safe defaults on error
        setPasswordProtectionEnabled(false);
        setAuthenticated(
          typeof window !== 'undefined' && 
          localStorage.getItem('pearl_vip_authenticated') === 'true'
        );
      } finally {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        setLoading(false);
      }
    };

    checkAccess();

    // Initialize analytics
    initAnalytics();
    trackPageView('/');

    // Inject JSON-LD structured data
    const schemas = [
      getOrganizationSchema(),
      getWebSiteSchema(),
      getPlaceSchema(),
      getFAQSchema(),
      getBreadcrumbSchema(),
    ];

    schemas.forEach((schema, index) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.text = JSON.stringify(schema);
      script.id = `schema-${index}`;
      document.head.appendChild(script);
    });

    return () => {
      // Cleanup schemas on unmount
      schemas.forEach((_, index) => {
        const script = document.getElementById(`schema-${index}`);
        if (script) script.remove();
      });
      // Restore scrolling on unmount
      document.body.style.overflow = '';
    };
  }, []);

  const handleSplashComplete = () => {
    // Mark splash as seen in localStorage
    if (typeof window !== 'undefined') {
      localStorage.setItem('pearl_splash_seen', 'true');
    }
    setShowSplash(false);
  };

  const handlePasswordSuccess = () => {
    setAuthenticated(true);
    document.body.style.overflow = '';
  };

  const handleVIPPreviewClick = () => {
    trackEvent('vip_preview_cta_click', { location: 'hero' });
    setPasswordGateOpen(true);
  };

  const handleWhatsAppClick = () => {
    window.open(`https://wa.me/${whatsappNumber}?text=${whatsappMessage}`, '_blank');
  };


  // Show splash screen first (if enabled and not seen before)
  if (showSplash) {
    return <SplashScreen onComplete={handleSplashComplete} />;
  }

  // Show loading state while checking access
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-lg text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Determine if we should show the gate (protection enabled AND not authenticated)
  // Safety check: if protection is disabled, never show gate
  const showGate = passwordProtectionEnabled === true && !authenticated;
  
  if (import.meta.env.DEV) {
    console.log('Render state:', { 
      passwordProtectionEnabled, 
      authenticated, 
      showGate,
      willShowNavBar: !showGate,
      willShowContent: !showGate
    });
    if (passwordProtectionEnabled === false && showGate === true) {
      console.warn('⚠️ WARNING: passwordProtectionEnabled is false but showGate is true - this should not happen!');
    }
  }

  return (
    <div className="min-h-screen">
      {/* Navigation - Hide when gate is active (logo is shown in hero instead) */}
      {!showGate && (
        <NavBar onRegisterClick={() => setLeadFormOpen(true)} onConsultClick={() => setMeetingOpen(true)} />
      )}

      {/* Main Content */}
      <main>
        <Hero 
          ref={heroRef}
          onRegisterClick={() => setLeadFormOpen(true)} 
          onConsultClick={() => setMeetingOpen(true)}
          onVIPPreviewClick={handleVIPPreviewClick}
          showVIPPreviewCTA={showGate}
        />
        
        {/* Only show rest of content when gate is not active */}
        {!showGate && (
          <>
            <VimeoEmbedSection />
            <BuyerPaths />
            <Residences 
              onAskSpecialist={() => setLeadFormOpen(true)} 
            />
            <LagoonAmenities />
            <LocationMap />
            <CTASection
              onRegisterClick={() => setLeadFormOpen(true)}
              onConsultClick={() => setMeetingOpen(true)}
              onWhatsAppClick={handleWhatsAppClick}
            />
          </>
        )}
      </main>

      {/* Footer - Hide when gate is active */}
      {!showGate && <Footer />}

      {/* Persistent Conversion UI - Hide when gate is active */}
      {!showGate && (
        <>
          <EnquiryRail 
            heroRef={heroRef}
            isAuthenticated={authenticated}
            passwordProtectionEnabled={passwordProtectionEnabled}
          />
          <StickyMobileCTA onClick={() => setLeadFormOpen(true)} />
          <WhatsAppButton onClick={handleWhatsAppClick} />
        </>
      )}

      {/* Modals */}
      <LeadForm open={leadFormOpen} onOpenChange={setLeadFormOpen} />
      <MeetingModal
        open={meetingOpen}
        onOpenChange={setMeetingOpen}
        onWhatsAppClick={handleWhatsAppClick}
      />
      <PasswordGate
        open={passwordGateOpen}
        onOpenChange={setPasswordGateOpen}
        onSuccess={handlePasswordSuccess}
      />
    </div>
  );
};

export default Index;
